import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  ConditionalRenderer,
  ScatterCharts,
  useLanguageTranslator,
} from "../../../components";
import { PlusIcon } from "../../../@lib/icons/plus";
import { MinusIcon } from "../../../@lib/icons/minus";
import {
  MagnifyingGlassIcon,
  ModalCloseIcon,
  QuestionMark,
  SearchDataIcon,
} from "../../../@lib/icons";
import { Filters } from "./filters";
import { gql, useLazyQuery } from "@apollo/client";
import { QuestionMarkNew } from "../../../@lib/icons/questionMarknew";
import WorkIcon from "../../../@lib/icons/customize-svgrepo-com.png";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "15px",
  maxHeight: "90dvh",
  overflowY: "auto",
  width: "800px",
  boxShadow: 24,
  py: 2,
};

const getCompositeCountryIndex = gql`
  query GetCompositeIndex(
    $countryId: ID!
    $themeId: ID!
    $startYear: Int!
    $endYear: Int!
    $catWeights: [CategoryWeightInput!]!
  ) {
    compositeIndexCountry(
      countryId: $countryId
      themeId: $themeId
      startYear: $startYear
      endYear: $endYear
      catWeights: $catWeights
    )
  }
`;

function CountryIndex({ switchHandler, filtersData }: any) {
  const [show, setShow] = useState(false);

  const [filters, setFilters] = useState<any>({
    country: "",
    startYear: "",
    endYear: "",
    theme: "",
    category: [],
  });

  const { translator } = useLanguageTranslator();

  const [
    fetchData,
    { data: countryIndexData = { compositeIndexCountry: [] }, error, loading },
  ] = useLazyQuery<
    { compositeIndexCountry: any },
    {
      countryId: string;
      themeId: string;
      startYear: number;
      endYear: number;
      catWeights: { categoryId: string; weight: number }[];
    }
  >(getCompositeCountryIndex, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.compositeIndexCountry?.length > 0) {
        setShow(false);
        return;
      }
    },
  });

  function onClickHandler() {
    setShow(true);
  }

  function handleClose() {
    setShow(false);
  }

  async function submitClickHandler() {
    await fetchData({
      variables: {
        countryId: filters?.country,
        themeId: filters?.theme,
        startYear: Number(filters?.startYear),
        endYear: Number(filters?.endYear),
        catWeights: filters?.category,
      },
    });
  }

  async function handleClearResults() {
    setFilters({
      country: "",
      startYear: "",
      endYear: "",
      theme: "",
      category: [],
    });

    await fetchData();
  }

  function changeCountryDataHandler() {
    setShow(true);
  }

  function handleCloseIcon() {
    setShow(false);
  }

  function switchClickHandler() {
    switchHandler();
    setShow(false);
  }

  return (
    <>
      <ConditionalRenderer
        condition={countryIndexData?.compositeIndexCountry?.length <= 0}
      >
        <Stack alignItems={"center"} justifyContent="center" ml={2} mr={2}>
          <Typography
            variant="h6"
            component="h1"
            sx={{ fontSize: "20px", textAlign: "center", width: "100%" }}
            my={1}
          >
            <span style={{ marginLeft: "10px" }}>
              {" "}
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSRXLWLJMxRXstG70EPaytYoPyiaOn1T1PWEA&s"
                width="40px"
                height="40px"
              />
            </span>
            {translator(`What is`)} "{translator(`My Custom Indicators`)}"
            <QuestionMarkNew />
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            {translator(`defineCustomOne`)}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            {translator(`defineCustomTwo`)}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{
              fontSize: "20px",
              fontWeight: "700",
              textAlign: "center",
              width: "100%",
            }}
            mb={1}
          >
            <img src={WorkIcon} width={"40px"} height={"40px"} />{" "}
            <span style={{ marginLeft: "10px" }}>
              {" "}
              {translator(`How does it work`)}
            </span>
            <QuestionMarkNew />
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            <span style={{ fontWeight: "700" }}>{translator(`Step`)} 1:</span>{" "}
            {translator(`step1`)}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            <span style={{ fontWeight: "700" }}>{translator(`Step`)} 2:</span>{" "}
            {translator(`step2`)}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            <span style={{ fontWeight: "700" }}>{translator(`Step`)} 3:</span>{" "}
            {translator(`step3`)}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            <span style={{ fontWeight: "700" }}>{translator(`Step`)} 4 {translator(`optional`)}):</span> (
            {translator(`step4`)}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            <span style={{ fontWeight: "700" }}>{translator(`Step`)} 5:</span>{" "}
            {translator(`step5`)}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{
              fontSize: "20px",
              fontWeight: "700",
              textAlign: "center",
              width: "100%",
            }}
            mb={1}
          >
             <MagnifyingGlassIcon />
            <span style={{ marginLeft: "10px" }}>
              {translator(`Ready to explore your data in a new way?`)}
            </span>
            <QuestionMarkNew />
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", textAlign: "left", width: "100%" }}
            mb={1}
          >
            {translator(
              `Click on the button below and start creating your custom indicators`
            )}
            <span style={{ fontWeight: "700", marginLeft:"5px" }}>
              {translator(`your custom indicators`)}
            </span>
          </Typography>
          <Button
            sx={{
              backgroundColor: "#F6A000",
              "&:hover": {
                backgroundColor: "#D48B00",
              },
              borderRadius: "10px",
              padding: "8px 50px",
              color: "white",
              marginTop: "20px",
            }}
            onClick={onClickHandler}
          >
            {translator(`Select Data`)}
          </Button>
        </Stack>
      </ConditionalRenderer>
      <Modal
        open={show}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Grid container sx={{ backgroundColor: "#F6F6F6" }} px={1} py={1}>
            <Grid item xs={12} md={6} lg={2} />
            <Grid item xs={12} md={6} lg={6}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" component="h2">
                  {translator(`Please Select the date to be shown`)}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  cursor: "pointer",
                }}
                onClick={handleCloseIcon}
              >
                <ModalCloseIcon />
              </div>
            </Grid>
          </Grid>
          <Box sx={{ px: 4, mt: 3 }}>
            <Filters
              setFilters={setFilters}
              filters={filters}
              my={0}
              isCountry={true}
              submitClickHandler={submitClickHandler}
              loading={loading}
              filtersData={filtersData}
            />

            <Stack alignItems={"center"} justifyContent="center" mt={2}>
              <Typography variant="h6" component="h2">
                {translator(`Want to switch`)}?
              </Typography>
              <Typography
                variant="h6"
                component="h2"
                style={{ fontWeight: "400" }}
              >
                {translator(`View data by category of indicator`)}
              </Typography>
              <Button
                size="medium"
                onClick={switchClickHandler}
                sx={{
                  color: "#F6A000",
                  textDecoration: "underline",
                  margin: "0px 20px",
                  height: "30px",
                }}
              >
                {translator(`Switch`)}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
      <ConditionalRenderer
        condition={
          countryIndexData?.compositeIndexCountry?.length > 0 && !loading
        }
      >
        <Stack direction="row" justifyContent={"center"}>
          <Typography variant="h6" component="h1">
            {translator(`Country`)} :{" "}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", marginLeft: "6px" }}
          >
            {
              filtersData?.data?.countryOptions?.find(
                (elem: any) => elem?.value === filters?.country
              )?.label
            }
          </Typography>
          <Typography variant="h6" component="h1" style={{ marginLeft: "6px" }}>
            {translator(`Theme`)} :{" "}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", marginLeft: "6px" }}
          >
            {
              filtersData?.data?.themeOptions?.find(
                (elem: any) => elem?.value === filters?.theme
              )?.label
            }
          </Typography>
          <Typography variant="h6" component="h1" style={{ marginLeft: "6px" }}>
            {translator(`Period`)} :{" "}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "400", marginLeft: "6px" }}
          >
            {filters?.startYear} - {filters?.endYear}
          </Typography>
        </Stack>
        <ScatterCharts data={countryIndexData?.compositeIndexCountry} />

        <Stack direction={"row"} justifyContent={"center"} px={30} mt={3}>
          <Button
            fullWidth
            size="large"
            onClick={handleClearResults}
            sx={{
              backgroundColor: "#E5E5EA",
              color: "#989C9F",
            }}
          >
            {translator(`Clear`)}
          </Button>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={changeCountryDataHandler}
            sx={{
              backgroundColor: "#F6A000",
              margin: "0px 20px",
            }}
          >
            {translator(`Change Data`)}
          </Button>
        </Stack>
      </ConditionalRenderer>
    </>
  );
}

export { CountryIndex };